import { IkeGeodesic } from '@ikegps/core-measurement';
import {
	IkeFieldType,
	type IkeProviderSubFormCopy,
	IkeProviderType,
	type LocationData,
	SubFormType,
} from '@ikegps/core-api';
import {
	CaptureProviderHelpers,
	type CollectionModel,
	type CollectionModelDrawableMarker,
	CollectionModelFieldHeight,
	type CollectionModelFieldSubForm,
	type CollectionModelFieldSubFormInstance,
	CollectionModelFieldVector,
	type FormModelField,
	type FormModelFieldSubForm,
	IkeCalcExistingSpanLegacyProvider,
	type Log,
	type ModelFieldStateHeight,
	readSpanForm,
	SpanSubFormAdaptor,
	SpanSubFormCopyProvider,
} from '@ikegps/core-shared';
import type { IkeClientService } from '../index';
import { FieldTransform } from './field.transform';
import { Logger } from '../log.service';

export interface SubFormLink {
	collection: CollectionModel;
	instance: CollectionModelFieldSubFormInstance;
	distance: number;
}

/**
 * Options for copying a subform instance.
 */
export interface CopyInstanceOptions {
	/** The instance to copy FROM */
	sourceInstance: CollectionModelFieldSubFormInstance;

	/** The subform field to copy TO */
	targetSubForm: CollectionModelFieldSubForm;

	/** If provided, update this existing instance; if omitted, create new */
	targetInstanceId?: string;

	/** If provided, enables capture cleanup (removes references to external captures) */
	sourceCollection?: CollectionModel;
}

/**
 * Options for performing a two-way span link.
 */
export interface TwoWaySpanLinkOptions {
	/** The span instance to copy FROM */
	sourceSpanSubForm: SpanSubFormAdaptor;

	/** The collection containing the source span */
	sourceCollection: CollectionModel;

	/** The collection to copy the span TO */
	targetCollection: CollectionModel;

	/** If provided, update this existing span; if omitted, create new */
	targetInstanceId?: string;
}

/**
 * Result of a successful two-way span link operation.
 */
export interface TwoWaySpanLinkResult {
	sourceCollection: CollectionModel;
	targetCollection: CollectionModel;
	sourceSpanInstance: CollectionModelFieldSubFormInstance;
	targetSpanInstance: CollectionModelFieldSubFormInstance;
}

/**
 * A reciprocal span link found on the target collection that links back to the source.
 */
export interface ReciprocalSpanLink {
	targetCollection: CollectionModel;
	targetSpanInstance: CollectionModelFieldSubFormInstance;
	targetVectorField: CollectionModelFieldVector;
}

export class SubFormLinkService implements IkeClientService {
	__priority = 100;

	log: Log = Logger;

	isSubFormCompatible(form: FormModelFieldSubForm): boolean {
		if (form.subFormType === SubFormType.SPAN) {
			return form.spanValidator.canLink;
		}
		return this.getRules(form) != null;
	}

	findLinks(collection: CollectionModel, candidateCollections: CollectionModel[]): SubFormLink[] {
		const subFormLinks: SubFormLink[] = [];
		const location = collection.location;

		const fields: FormModelField[] = collection.schema.fields.slice();
		while (fields.length > 0) {
			const fieldSchema = fields.pop();

			if (!fieldSchema) {
				continue;
			}

			if (fieldSchema.type !== IkeFieldType.FORM) {
				continue;
			}

			const subform = collection.getField<CollectionModelFieldSubForm>(fieldSchema.id);
			const rules = this.getRules(subform.schema);
			if (rules == null) {
				// FIXME: This seems wrong -- it adds instance IDs to list of field IDs, so nested subforms are never actually searched
				fields.push.apply(fields, subform.value);
				continue;
			}

			for (const candidateCollection of candidateCollections) {
				const subFormLink = this.getLink(rules, subform, location, candidateCollection);
				if (subFormLink !== null) {
					subFormLinks.push(subFormLink);
					continue;
				}
			}
		}

		return subFormLinks;
	}

	/**
	 * Legacy method for pull-flow span linking.
	 * @deprecated Use copyInstance() directly
	 */
	copySubForm(collection: CollectionModel, subFormLinkRecord: SubFormLink): CollectionModelFieldSubFormInstance {
		const targetSubForm = collection.getField<CollectionModelFieldSubForm>(subFormLinkRecord.instance.schema.id);

		return this.copyInstance({
			sourceInstance: subFormLinkRecord.instance,
			targetSubForm,
			sourceCollection: subFormLinkRecord.collection,
		});
	}

	/**
	 * Copy a subform instance to a target subform, applying field transformations.
	 * Supports both creating a new instance and updating an existing one.
	 *
	 * Field transformations applied:
	 * - Bearing inversion (180 degree flip)
	 * - Span type replacement (NextPole to PreviousPole, OtherPole unchanged)
	 * - Attachment height exclusions (cleared, not copied)
	 *
	 * @param options Configuration for the copy operation
	 * @returns The target instance (created or existing)
	 * @throws Error if targetInstanceId is provided but not found
	 */
	copyInstance(options: CopyInstanceOptions): CollectionModelFieldSubFormInstance {
		const { sourceInstance, targetSubForm, targetInstanceId, sourceCollection } = options;

		// Get or create target instance
		let targetInstance: CollectionModelFieldSubFormInstance;

		if (targetInstanceId) {
			// Update existing instance
			const existing = targetSubForm.getInstance(targetInstanceId);
			if (!existing) {
				throw new Error(`Target instance ${targetInstanceId} not found`);
			}
			targetInstance = existing;
		} else {
			// Always create a new instance with a fresh ID. Using the source's ID
			// would collide with an existing span if the target was duplicated from
			// the source or previously linked to it.
			const newInstanceId = targetSubForm.Mutator.create();
			const created = targetSubForm.getInstance(newInstanceId);
			if (!created) {
				throw new Error('Failed to create target instance');
			}
			targetInstance = created;
		}

		// Save height measurements from target before overwriting (update existing flow only).
		// The copy pipeline clears pole-specific heights (attachment, bolt, etc.) since they
		// shouldn't carry over from the source. But if the target already had measurements
		// taken on its own photos, we want to restore those after the copy.
		//
		// After saving, we null out the saved fields' state values. This is critical:
		// ModelMutatorSubForm.copy() calls clear() which calls manual(null) on each field.
		// For photomeasure fields, manual(null) calls unlink() which calls label.unlink(fieldId).
		// If that was the label's last link, label.remove() destroys BOTH the label AND its
		// parent marker drawable. By nulling the state first, isPhotoMeasure() returns false
		// during clear(), unlink() is skipped, and the drawables survive.
		const savedHeightStates = new Map<string, ModelFieldStateHeight>();
		if (targetInstanceId) {
			targetInstance.forEachField((field) => {
				if (
					CollectionModelFieldHeight.isHeightField(field) &&
					(field.isPhotoMeasure() || field.isPhotoMeasureManual())
				) {
					const state = field.stateValue;
					if (state?.drawable && state?.captureId) {
						savedHeightStates.set(field.id, { ...state });
						targetInstance.collection.state.setFieldValue(field.id, null);
					}
				}
			});
		}

		// Copy all field values from source to target
		this.copyFields(sourceInstance, targetInstance);

		// Apply transformation rules (bearing inversion, span type replacement, etc.)
		const rules = this.getRules(targetSubForm.schema);
		this.applyFieldTransforms(targetInstance, rules);

		// Clean up external capture references if sourceCollection provided
		if (sourceCollection) {
			this.cleanupExternalCaptureReferences(sourceCollection, targetInstance);
		}

		// Restore height measurements that were cleared by the pipeline but belong
		// to the target collection's own captures (not the source's).
		for (const [fieldId, savedState] of savedHeightStates) {
			if (targetInstance.collection.state.getFieldValue(fieldId) != null) {
				continue; // Field survived the pipeline — don't overwrite
			}

			try {
				const drawableState = targetInstance.collection.getDrawableState(savedState.captureId!);
				if (!drawableState.hasDrawable(savedState.drawable!)) {
					continue; // Drawable was removed
				}

				// Restore state value
				targetInstance.collection.state.setFieldValue(fieldId, savedState);

				// Re-link the drawable's label to the field if not already linked.
				// The label may still have the link from before (we nulled the state
				// but didn't remove the label link), so check to avoid duplicates.
				const drawable = drawableState.getDrawable<CollectionModelDrawableMarker>(savedState.drawable!);
				const alreadyLinked = drawable.label.links.some((link) => link.id === fieldId);
				if (!alreadyLinked) {
					targetInstance.forEachField((field) => {
						if (field.id === fieldId && CollectionModelFieldHeight.isHeightField(field)) {
							drawable.label.linkHeight(field);
							return false;
						}
						return true;
					});
				}
			} catch {
				// Drawable or capture no longer exists — skip
			}
		}

		return targetInstance;
	}

	/**
	 * Perform a complete two-way span link operation:
	 * 1. Copy span data from source to target with transformations
	 * 2. Set bidirectional vector links between collections
	 * 3. Clean up external capture references
	 *
	 * @param options Configuration for the two-way link
	 * @returns Result containing both collections and span instances
	 * @throws Error if target collection has no span subform
	 */
	performTwoWaySpanLink(options: TwoWaySpanLinkOptions): TwoWaySpanLinkResult {
		const { sourceSpanSubForm, sourceCollection, targetCollection, targetInstanceId } = options;

		// Get target span field
		const targetSpanField = targetCollection.spanSubForm;
		if (!targetSpanField) {
			throw new Error('Target collection has no span subform');
		}

		// Copy span data with transforms and capture cleanup
		const targetSpanInstance = this.copyInstance({
			sourceInstance: sourceSpanSubForm.instance,
			targetSubForm: targetSpanField,
			targetInstanceId,
			sourceCollection,
		});

		// Set up bidirectional vector links
		// Source → Target
		sourceSpanSubForm.spanVectorField.Mutator.collection(targetCollection.id);

		// Target → Source
		const targetSpanAdaptor = new SpanSubFormAdaptor(targetSpanInstance);
		targetSpanAdaptor.spanVectorField.Mutator.collection(sourceCollection.id);

		return {
			sourceCollection,
			targetCollection,
			sourceSpanInstance: sourceSpanSubForm.instance,
			targetSpanInstance,
		};
	}

	/**
	 * Find the reciprocal span link on the target collection.
	 * Given a span vector field that links to another collection, checks whether
	 * the target collection has a span that links back to the source.
	 *
	 * @returns The reciprocal link info, or null if no two-way link exists
	 */
	async findReciprocalLink(
		field: CollectionModelFieldVector,
		sourceCollection: CollectionModel
	): Promise<ReciprocalSpanLink | null> {
		const targetCollection = await field.getLinkedCollection();
		if (!targetCollection) {
			return null;
		}

		const targetSpanSubForm = targetCollection.spanSubForm;
		if (!targetSpanSubForm) {
			return null;
		}

		for (const instance of targetSpanSubForm.value) {
			try {
				const adaptor = new SpanSubFormAdaptor(instance);
				if (adaptor.spanTargetCollectionId === sourceCollection.id) {
					return {
						targetCollection,
						targetSpanInstance: instance,
						targetVectorField: adaptor.spanVectorField,
					};
				}
			} catch {
				// Instance is not a valid SPAN subform - skip
				continue;
			}
		}

		return null;
	}

	/**
	 * Search for SubForm instances on the candidateCollection which pass the given rules.
	 * The candidateCollection must also be within the rule's distance of the given location.
	 * @param rules Defines max distance and the vector field used to search for a linkable vector end point.
	 * @param subform The subForm instance which we are searching from - the "end" of the link.
	 * @param location The location of the collection that the subform instance (end point) exists on.
	 * @param candidateCollection The collection we are searching for a vector field on whose end point is within range of the location.
	 * @returns SubFormLink describing the link.
	 */
	public getLink(
		rules: IkeProviderSubFormCopy,
		subform: CollectionModelFieldSubForm,
		location: LocationData,
		candidateCollection: CollectionModel
	): SubFormLink | null {
		const candidateSubForm = candidateCollection.getOptionalField<CollectionModelFieldSubForm>(subform.schema.id);
		if (candidateSubForm == null || !candidateSubForm.hasValue()) {
			return null;
		}

		const candidateLocation = candidateCollection.location;

		const subFormLinks: SubFormLink[] = [];
		for (const candidateInstance of candidateSubForm.value) {
			// Skip if the instance ID of the candidate is the same as the current subform instance.
			// NOTE: This might not be desired behavior in some situations, since instance IDs are preserved when duplicating collections.
			if (subform.getInstance(candidateInstance.instanceId) != null) {
				this.log.warn(
					{ collection: candidateCollection.id, instance: candidateInstance.instanceId },
					`Skipping SubForm link candidate because it had the same instanceId: ${candidateCollection.title} (${candidateInstance.instanceId})`
				);
				continue;
			}

			// Check if any rules reject any field in the candidate instance
			if (!this.matchesRules(rules, candidateInstance)) {
				this.log.info(
					{ collection: candidateCollection.id, instance: candidateInstance.instanceId, rules },
					`Skipping SubForm link candidate because it failed rules check: ${candidateCollection.title} (${candidateInstance.instanceId})`
				);
				continue;
			}

			const vectorField = candidateInstance.getField<CollectionModelFieldVector>(rules.vector);
			const distance = this.getDistance(candidateLocation, vectorField, location);

			if (distance == null || distance >= rules.distance) {
				this.log.info(
					{ collection: candidateCollection.id, instance: candidateInstance.instanceId, rules, distance },
					`Skipping SubForm link candidate because the endpoint was too far away: ${candidateCollection.title} (${candidateInstance.instanceId})`
				);
				continue;
			}

			subFormLinks.push({
				collection: candidateCollection,
				instance: candidateInstance,
				distance,
			});
		}

		subFormLinks.sort(this.sortSubFormLinkRecords);
		return subFormLinks[0] ?? null;
	}

	private matchesRules(rules: IkeProviderSubFormCopy, instance: CollectionModelFieldSubFormInstance): boolean {
		return instance.forEachField((field) => {
			const fieldRule = rules.fields[field.schema.id];
			if (!fieldRule) {
				return true;
			}
			const transform = FieldTransform.create(field, fieldRule);
			if (transform == null) {
				return true;
			}

			const match = transform.match(field);

			if (!match) {
				this.log.info(
					`Rule match failed: ${transform.constructor.name} (field=${field.schema.id}, instance=${instance.instanceId})`
				);
			}

			return match;
		});
	}

	private getDistance(
		sourceLocation: LocationData,
		sourceVectorField: CollectionModelFieldVector,
		targetLocation: LocationData
	): number | null {
		let spanLocation1: LocationData;
		let spanLocation2: LocationData;

		if (sourceVectorField.provider === CollectionModelFieldVector.PROVIDER_P2P) {
			const captures = sourceVectorField.getCaptures();
			spanLocation1 = captures[0]!.target;
			spanLocation2 = captures[1]!.target;
		} else if (
			sourceVectorField.provider === CollectionModelFieldVector.PROVIDER_MANUAL ||
			sourceVectorField.provider === CollectionModelFieldVector.PROVIDER_COLLECTION
		) {
			spanLocation1 = IkeGeodesic.projectLocation(
				sourceLocation,
				sourceVectorField.value.bearing,
				sourceVectorField.value.distance
			);
			spanLocation2 = sourceLocation != null ? sourceLocation : spanLocation1;
		} else {
			return null;
		}

		const distance1 = IkeGeodesic.getDistance(targetLocation, spanLocation1);
		const distance2 = IkeGeodesic.getDistance(targetLocation, spanLocation2);
		return Math.min(distance1, distance2);
	}

	/**
	 * Gets all capture ids for the collection ignoring the provided subform
	 * @param collection The collection model
	 * @param subformInstance The instance of the subform which you want the captures removed
	 * @returns An array of captures which exist on the collection, but not the provided subform instance
	 */
	private getCaptureIdsExceptForSubForm(
		collection: CollectionModel,
		subformInstance: CollectionModelFieldSubFormInstance
	): { [id: string]: boolean } {
		const captureIds: { [id: string]: boolean } = {};

		collection.forEachField((field) => {
			if (CaptureProviderHelpers.isCaptureProvider(field)) {
				for (const capture of field.getCaptures()) {
					captureIds[capture.objectId] = true;
				}
			}
		});

		subformInstance.forEachField((field) => {
			if (CaptureProviderHelpers.isCaptureProvider(field)) {
				for (const capture of field.getCaptures()) {
					delete captureIds[capture.objectId];
				}
			}
		});

		return captureIds;
	}

	/**
	 * Remove height field references to captures that exist on sourceCollection
	 * but not on the target collection (where targetInstance lives).
	 */
	private cleanupExternalCaptureReferences(
		sourceCollection: CollectionModel,
		targetInstance: CollectionModelFieldSubFormInstance
	): void {
		const externalCaptureIds = this.getCaptureIdsExceptForSubForm(sourceCollection, targetInstance);

		targetInstance.forEachField((field) => {
			if (!CollectionModelFieldHeight.isHeightField(field) || !field.isPhotoMeasure()) {
				return;
			}

			const stateValue = field.stateValue;
			if (!stateValue?.captureId || !stateValue?.drawable) {
				return;
			}

			const drawableState = targetInstance.collection.getDrawableState(stateValue.captureId);
			const drawable = drawableState.getDrawable(stateValue.drawable);
			if (externalCaptureIds[drawable.captureId]) {
				field.Mutator.unlink();
			}
		});
	}

	private sortSubFormLinkRecords(record1: SubFormLink, record2: SubFormLink): number {
		return record1.distance - record2.distance;
	}

	/**
	 * Copy all field values from source instance to target instance.
	 * Overwrites existing values on target (if source has a value).
	 */
	private copyFields(
		sourceInstance: CollectionModelFieldSubFormInstance,
		targetInstance: CollectionModelFieldSubFormInstance
	): void {
		sourceInstance.forEachField((sourceField) => {
			const targetField = targetInstance.getOptionalField(sourceField.schema.id);
			if (!targetField) {
				return;
			}

			if (sourceField.hasValue()) {
				targetField.Mutator.copy(sourceField);
			} else {
				targetField.Mutator.clear();
			}
		});
	}

	/**
	 * Apply transformation rules to all fields in an instance.
	 */
	private applyFieldTransforms(
		instance: CollectionModelFieldSubFormInstance,
		rules: IkeProviderSubFormCopy | null
	): void {
		if (!rules) {
			return;
		}

		instance.forEachField((field) => {
			const fieldRule = rules.fields[field.schema.id];
			if (!fieldRule) {
				return;
			}

			const transform = FieldTransform.create(field, fieldRule);
			if (transform === null) {
				return;
			}

			transform.apply(field);
		});
	}

	public getRules(form: FormModelFieldSubForm): IkeProviderSubFormCopy | null {
		if (form.subFormType === SubFormType.SPAN) {
			// A broken span configuration must not produce copy/link rules
			if (!form.spanValidator.canLink) {
				return null;
			}
			const spanSubForm = readSpanForm(form.getForm());
			if (!spanSubForm) return null;
			return new SpanSubFormCopyProvider(spanSubForm);
		}

		for (const provider of form.providers ?? []) {
			if (provider == null) {
				continue;
			}

			if (typeof provider === 'object') {
				if (provider.type === IkeProviderType.EXISTING_SPAN) {
					return provider as IkeProviderSubFormCopy;
				}
			} else if (provider === IkeProviderType.EXISTING_SPAN) {
				return IkeCalcExistingSpanLegacyProvider;
			}
		}

		return null;
	}
}
